/** @format */

import { useState, useEffect, useCallback } from "react";
import { collection, getDocs, doc, getDoc } from "firebase/firestore";
import { db } from "../firebase/config";
import { useAuth } from "../contexts/AuthContext";

export const useMyBooks = () => {
  const [purchasedBooks, setPurchasedBooks] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { currentUser } = useAuth();

  // Function to fetch books from the nested structure: Mybooks/userid/books/bookid
  const fetchMyBooks = useCallback(async () => {
    console.log("=== STARTING NESTED MYBOOKS FETCH ===");
    
    if (!currentUser) {
      console.log("No current user");
      setPurchasedBooks([]);
      return;
    }

    console.log("Current user:", currentUser.uid);

    try {
      setLoading(true);
      setError(null);

      // Access the nested structure: Mybooks/userid/books
      const userBooksRef = collection(db, "Mybooks", currentUser.uid, "books");
      console.log("Accessing Mybooks collection at path:", `Mybooks/${currentUser.uid}/books`);
      
      const snapshot = await getDocs(userBooksRef);
      console.log("Total books found for user:", snapshot.size);
      
      const allData: any[] = [];
      
      // Process each book entry and fetch the actual book details
      for (const docSnapshot of snapshot.docs) {
        const bookEntry = docSnapshot.data();
        console.log("Book Entry:", bookEntry);
        
        if (bookEntry.bookid) {
          try {
            // Fetch the actual book details from the books collection
            const bookDocRef = doc(db, "books", bookEntry.bookid);
            const bookDoc = await getDoc(bookDocRef);
            
            if (bookDoc.exists()) {
              const bookData = bookDoc.data();
              console.log("Full Book Data:", bookData);
              
              allData.push({
                ...bookData,
                id: bookEntry.bookid,
                purchaseTimestamp: bookEntry.timestamp,
                mybooksId: docSnapshot.id,
                // Convert Firestore timestamps to JavaScript Date objects
                createdAt: bookData.createdAt?.toDate() || bookData.createdAt || new Date(),
                updatedAt: bookData.updatedAt?.toDate() || bookData.updatedAt || new Date(),
              });
            } else {
              console.log("Book not found with ID:", bookEntry.bookid);
              // Still add the entry data if book not found
              allData.push({
                ...bookEntry,
                id: bookEntry.bookid,
                purchaseTimestamp: bookEntry.timestamp,
                mybooksId: docSnapshot.id,
                createdAt: new Date(),
                updatedAt: new Date(),
              });
            }
          } catch (err) {
            console.error("Error fetching book details:", err);
            // Add entry data even if there's an error
            allData.push({
              ...bookEntry,
              id: bookEntry.bookid,
              purchaseTimestamp: bookEntry.timestamp,
              mybooksId: docSnapshot.id
            });
          }
        }
      }

      console.log("All collected book data:", allData);
      setPurchasedBooks(allData);
      
    } catch (err) {
      console.error("Error fetching Mybooks:", err);
      setError("Failed to load Mybooks data");
    } finally {
      setLoading(false);
    }
    
    console.log("=== END NESTED MYBOOKS FETCH ===");
  }, [currentUser]);

  // Refresh function
  const refreshBooks = useCallback(() => {
    fetchMyBooks();
  }, [fetchMyBooks]);

  // Load on mount
  useEffect(() => {
    fetchMyBooks();
  }, [fetchMyBooks]);

  return {
    purchasedBooks,
    loading,
    error,
    refreshBooks,
  };
};
